import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { SyncToJobberButton } from './JobberAutoFill';

// Wire sizing table (CEC 2024, 75°C column)
const WIRE_TABLE = {
  copper: {
    14: 20, 12: 25, 10: 35, 8: 50, 6: 65, 4: 85, 3: 100, 2: 115, 1: 130,
    '1/0': 150, '2/0': 175, '3/0': 200, '4/0': 230
  },
  aluminum: {
    8: 40, 6: 50, 4: 65, 3: 75, 2: 90, 1: 100,
    '1/0': 120, '2/0': 135, '3/0': 155, '4/0': 180, '250kcmil': 205
  }
};

// Material pricing database
const MATERIAL_PRICING = {
  wire: {
    '14 copper': 0.25,
    '12 copper': 0.35,
    '10 copper': 0.55,
    '8 copper': 1.10,
    '6 copper': 1.65,
    '4 copper': 2.45,
    '3 copper': 3.15,
    '2 copper': 4.20,
    '1 copper': 5.50,
    '1/0 copper': 7.00,
    '2/0 copper': 9.00,
    '3/0 copper': 11.50,
    '4/0 copper': 14.50,
    '8 aluminum': 0.65,
    '6 aluminum': 0.95,
    '4 aluminum': 1.45,
    '3 aluminum': 1.85,
    '2 aluminum': 2.40,
    '1 aluminum': 3.00,
    '1/0 aluminum': 3.85,
    '2/0 aluminum': 4.85,
    '3/0 aluminum': 6.00,
    '4/0 aluminum': 7.50,
    '250kcmil aluminum': 8.75
  },
  conduit: {
    '3/4 PVC': 0.85,
    '1 PVC': 1.15,
    '1.25 PVC': 1.55,
    '1.5 PVC': 2.00,
    '3/4 EMT': 1.25,
    '1 EMT': 1.75,
    '1.25 EMT': 2.25,
    '1.5 EMT': 2.85
  },
  boxes: {
    'breaker box 200A': 350,
    'breaker box 100A': 180,
    'disconnect 60A': 85,
    'disconnect 100A': 120,
    'sub-panel 100A': 280,
    'sub-panel 200A': 450
  },
  other: {
    'DCC-12 load management': 1200,
    'EV charger hardware kit': 450,
    'ATS dual-fuel': 650,
    'EV charger bracket kit': 75,
    'disconnect/safety switch': 95,
    'flex conduit 3/4': 1.20,
    'wire connector pack': 15,
    'electrical tape': 4.50,
    'inspection/permit fee': 200
  }
};

const STYLES = {
  container: {
    maxWidth: '480px',
    margin: '0 auto',
    padding: '16px',
    backgroundColor: '#0d0f12',
    color: '#e8eaf0',
    fontFamily: "'Roboto', sans-serif",
    minHeight: '100vh'
  },
  header: {
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '1px solid #2a3140'
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: '#a5ce3a',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '12px',
    fontWeight: '500',
    padding: '0'
  },
  title: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: '28px',
    margin: '0 0 8px 0',
    color: '#a5ce3a',
    letterSpacing: '0.5px'
  },
  subtitle: {
    fontSize: '14px',
    color: '#8892a4',
    margin: '4px 0'
  },
  section: {
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: '#161a20',
    border: '1px solid #2a3140',
    borderRadius: '6px'
  },
  sectionTitle: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: '16px',
    margin: '0 0 16px 0',
    color: '#a5ce3a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px'
  },
  tableHeader: {
    backgroundColor: '#1e2328',
    borderBottom: '2px solid #2a3140',
    padding: '10px',
    textAlign: 'left',
    fontWeight: '700',
    color: '#a5ce3a',
    fontSize: '12px',
    textTransform: 'uppercase'
  },
  tableCell: {
    padding: '10px',
    borderBottom: '1px solid #2a3140',
    textAlign: 'left'
  },
  tableRow: {
    backgroundColor: '#161a20'
  },
  tableRowHover: {
    backgroundColor: '#1e2328'
  },
  input: {
    width: '100%',
    padding: '8px',
    backgroundColor: '#0d0f12',
    border: '1px solid #2a3140',
    color: '#e8eaf0',
    borderRadius: '4px',
    fontSize: '13px',
    boxSizing: 'border-box'
  },
  button: {
    padding: '10px 16px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '13px',
    transition: 'background-color 0.2s'
  },
  buttonPrimary: {
    backgroundColor: '#a5ce3a',
    color: '#0d0f12',
    marginRight: '8px'
  },
  buttonSecondary: {
    backgroundColor: '#36383c',
    color: '#e8eaf0',
    marginRight: '8px'
  },
  buttonDanger: {
    backgroundColor: '#c74c4c',
    color: '#fff',
    fontSize: '12px',
    padding: '6px 10px'
  },
  totalsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #2a3140'
  },
  totalsLabel: {
    fontWeight: '600'
  },
  grandTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 0',
    borderTop: '2px solid #2a3140',
    fontSize: '18px',
    fontWeight: '700',
    color: '#a5ce3a'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px'
  },
  spinner: {
    display: 'inline-block',
    width: '24px',
    height: '24px',
    border: '3px solid #2a3140',
    borderTop: '3px solid #a5ce3a',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  errorContainer: {
    padding: '16px',
    backgroundColor: '#3d2626',
    border: '1px solid #8b4545',
    borderRadius: '4px',
    color: '#ff8888'
  },
  buttonsContainer: {
    display: 'flex',
    gap: '8px',
    marginTop: '20px',
    flexWrap: 'wrap'
  }
};

// Function to select wire gauge based on circuit size
function selectWireGauge(circuitSize, isAluminum = false) {
  const table = isAluminum ? WIRE_TABLE.aluminum : WIRE_TABLE.copper;
  const targetAmperage = parseInt(circuitSize);

  let selectedGauge = null;
  for (const [gauge, amperage] of Object.entries(table)) {
    if (amperage >= targetAmperage) {
      selectedGauge = gauge;
      break;
    }
  }

  return selectedGauge || (isAluminum ? '4/0' : '4/0');
}

export default function QuoteBuilder({ assessmentId: propAssessmentId, onBack }) {
  const navigate = useNavigate();
  const params = useParams();
  const assessmentId = propAssessmentId || params.id;
  const goBack = onBack || (() => navigate("/"));
  const [assessment, setAssessment] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quoteNumber, setQuoteNumber] = useState('');

  // Load assessment and generate line items
  useEffect(() => {
    loadAssessment();
  }, [assessmentId]);

  async function loadAssessment() {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('assessments')
        .select('*')
        .eq('id', assessmentId)
        .single();

      if (fetchError) throw fetchError;
      if (!data) throw new Error('Assessment not found');

      setAssessment(data);

      // Generate line items based on service type
      const items = generateLineItems(data);
      setLineItems(items);

      // Generate quote number (e.g., Q-2024-00001)
      const timestamp = Date.now().toString().slice(-5);
      const year = new Date().getFullYear();
      setQuoteNumber(`Q-${year}-${timestamp}`);
    } catch (err) {
      console.error('Error loading assessment:', err);
      setError(err.message || 'Failed to load assessment');
    } finally {
      setLoading(false);
    }
  }

  function generateLineItems(data) {
    const items = [];
    const serviceType = data.service_type;

    if (serviceType === 'ev_charger') {
      items.push(...generateEVChargerItems(data));
    } else if (serviceType === 'generator_install') {
      items.push(...generateGeneratorItems(data));
    } else if (serviceType === 'panel_upgrade') {
      items.push(...generatePanelUpgradeItems(data));
    } else {
      // Default/custom service
      items.push({
        id: 'labor-1',
        name: 'Electrical Work - Labour',
        qty: 4,
        unit: 'hours',
        unitCost: 75,
        markupRate: 2.4,
        type: 'labour',
        lineTotal: 0
      });
    }

    return calculateLineTotals(items);
  }

  function generateEVChargerItems(data) {
    const items = [];
    const services = data.additional_services?.ev_charger || {};
    const circuitSize = services.circuit_size || '50';
    const runDistance = services.run_distance_ft || 30;
    const loadMgmt = services.load_management_required;
    const permitRequired = services.permit_required;

    // Wire selection
    const wireGauge = selectWireGauge(circuitSize, false);
    const wireKey = `${wireGauge} copper`;
    const wireCost = MATERIAL_PRICING.wire[wireKey] || 1.50;

    const wireLength = Math.ceil(runDistance / 100) * 100; // Round to nearest 100ft spool
    items.push({
      id: 'wire-1',
      name: `#${wireGauge} Copper THHN/THWN (${wireLength}ft)`,
      qty: wireLength,
      unit: 'ft',
      unitCost: wireCost,
      markupRate: 0.20,
      type: 'material',
      lineTotal: 0
    });

    // Conduit
    const conduitSize = circuitSize >= 100 ? '1.25 PVC' : '1 PVC';
    const conduitCost = MATERIAL_PRICING.conduit[conduitSize] || 1.15;
    items.push({
      id: 'conduit-1',
      name: `${conduitSize} Conduit (${wireLength}ft)`,
      qty: wireLength,
      unit: 'ft',
      unitCost: conduitCost,
      markupRate: 0.20,
      type: 'material',
      lineTotal: 0
    });

    // EV charger hardware
    items.push({
      id: 'charger-hw',
      name: 'EV Charger Hardware Kit (Level 2, 240V)',
      qty: 1,
      unit: 'ea',
      unitCost: MATERIAL_PRICING.other['EV charger hardware kit'],
      markupRate: 0.35,
      type: 'material',
      lineTotal: 0
    });

    // Disconnect switch
    items.push({
      id: 'disconnect-1',
      name: 'Disconnect/Safety Switch 60A',
      qty: 1,
      unit: 'ea',
      unitCost: MATERIAL_PRICING.other['disconnect/safety switch'],
      markupRate: 0.20,
      type: 'material',
      lineTotal: 0
    });

    // Load management
    if (loadMgmt) {
      items.push({
        id: 'dcc-12',
        name: 'DCC-12 Load Management System',
        qty: 1,
        unit: 'ea',
        unitCost: MATERIAL_PRICING.other['DCC-12 load management'],
        markupRate: 0.35,
        type: 'material',
        lineTotal: 0
      });
    }

    // Permit
    if (permitRequired) {
      items.push({
        id: 'permit',
        name: 'Inspection & Permit Fee',
        qty: 1,
        unit: 'ea',
        unitCost: MATERIAL_PRICING.other['inspection/permit fee'],
        markupRate: 0.00,
        type: 'material',
        lineTotal: 0
      });
    }

    // Labour
    const labourHours = 8 + (runDistance > 100 ? 2 : 0) + (loadMgmt ? 1 : 0);
    items.push({
      id: 'labour-1',
      name: 'EV Charger Installation - Labour',
      qty: labourHours,
      unit: 'hours',
      unitCost: 75,
      markupRate: 2.4,
      type: 'labour',
      lineTotal: 0
    });

    return items;
  }

  function generateGeneratorItems(data) {
    const items = [];
    const fuelType = data.fuel_type || 'propane';
    const mainBreaker = data.main_breaker_rating || 200;
    const hasATS = data.ats_required;

    // Generator hardware (placeholder pricing)
    const generatorCost = mainBreaker >= 200 ? 3500 : 2500;
    items.push({
      id: 'gen-1',
      name: `${fuelType.toUpperCase()} Generator ${mainBreaker}A`,
      qty: 1,
      unit: 'ea',
      unitCost: generatorCost,
      markupRate: 0.25,
      type: 'material',
      lineTotal: 0
    });

    // ATS
    if (hasATS) {
      items.push({
        id: 'ats-1',
        name: 'Automatic Transfer Switch (ATS)',
        qty: 1,
        unit: 'ea',
        unitCost: MATERIAL_PRICING.other['ATS dual-fuel'],
        markupRate: 0.25,
        type: 'material',
        lineTotal: 0
      });
    }

    // Conduit and wiring (to generator)
    items.push({
      id: 'conduit-1',
      name: '1.5 PVC Conduit (100ft)',
      qty: 100,
      unit: 'ft',
      unitCost: MATERIAL_PRICING.conduit['1.5 PVC'],
      markupRate: 0.20,
      type: 'material',
      lineTotal: 0
    });

    items.push({
      id: 'wire-1',
      name: '#2 Copper THHN/THWN (100ft)',
      qty: 100,
      unit: 'ft',
      unitCost: MATERIAL_PRICING.wire['2 copper'],
      markupRate: 0.20,
      type: 'material',
      lineTotal: 0
    });

    // Labour
    items.push({
      id: 'labour-1',
      name: 'Generator Installation - Labour',
      qty: 16,
      unit: 'hours',
      unitCost: 75,
      markupRate: 2.4,
      type: 'labour',
      lineTotal: 0
    });

    return items;
  }

  function generatePanelUpgradeItems(data) {
    const items = [];
    const newMainBreaker = data.new_main_breaker_rating || 200;

    // New panel
    const panelKey = newMainBreaker >= 200 ? 'breaker box 200A' : 'breaker box 100A';
    items.push({
      id: 'panel-1',
      name: `${newMainBreaker}A Main Panel`,
      qty: 1,
      unit: 'ea',
      unitCost: MATERIAL_PRICING.boxes[panelKey],
      markupRate: 0.25,
      type: 'material',
      lineTotal: 0
    });

    // Breakers
    items.push({
      id: 'breakers-1',
      name: 'Breaker Pack (various ratings)',
      qty: 1,
      unit: 'set',
      unitCost: 150,
      markupRate: 0.20,
      type: 'material',
      lineTotal: 0
    });

    // Labour
    items.push({
      id: 'labour-1',
      name: 'Panel Upgrade - Labour',
      qty: 6,
      unit: 'hours',
      unitCost: 75,
      markupRate: 2.4,
      type: 'labour',
      lineTotal: 0
    });

    return items;
  }

  function calculateLineTotals(items) {
    return items.map(item => {
      let lineTotal;
      if (item.type === 'labour') {
        // Labour: qty × $75 × markup = billing rate
        lineTotal = item.qty * item.unitCost * item.markupRate;
      } else {
        // Material: qty × unitCost × (1 + markup)
        const subtotal = item.qty * item.unitCost;
        lineTotal = subtotal * (1 + item.markupRate);
      }
      return { ...item, lineTotal: parseFloat(lineTotal.toFixed(2)) };
    });
  }

  function updateLineItem(id, field, value) {
    const updated = lineItems.map(item => {
      if (item.id === id) {
        const newItem = { ...item, [field]: value };
        // Recalculate line total
        if (field === 'qty' || field === 'unitCost' || field === 'markupRate') {
          if (item.type === 'labour') {
            newItem.lineTotal = parseFloat((newItem.qty * newItem.unitCost * newItem.markupRate).toFixed(2));
          } else {
            const subtotal = newItem.qty * newItem.unitCost;
            newItem.lineTotal = parseFloat((subtotal * (1 + newItem.markupRate)).toFixed(2));
          }
        }
        return newItem;
      }
      return item;
    });
    setLineItems(updated);
  }

  function removeLineItem(id) {
    setLineItems(lineItems.filter(item => item.id !== id));
  }

  function addLineItem() {
    const newId = `item-${Date.now()}`;
    const newItem = {
      id: newId,
      name: 'New Item',
      qty: 1,
      unit: 'ea',
      unitCost: 0,
      markupRate: 0.20,
      type: 'material',
      lineTotal: 0
    };
    setLineItems([...lineItems, newItem]);
  }

  // Calculate totals
  const materialItems = lineItems.filter(i => i.type === 'material');
  const labourItems = lineItems.filter(i => i.type === 'labour');

  const materialSubtotal = materialItems.reduce((sum, i) => sum + i.lineTotal, 0);
  const labourSubtotal = labourItems.reduce((sum, i) => sum + i.lineTotal, 0);
  const pst = materialSubtotal * 0.07;
  const grandTotal = materialSubtotal + labourSubtotal + pst;

  if (loading) {
    return (
      <div style={STYLES.container}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Roboto:wght@400;500;700&display=swap');
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
        <div style={STYLES.loadingContainer}>
          <div style={STYLES.spinner}></div>
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div style={STYLES.container}>
        <button style={STYLES.backButton} onClick={goBack}>← Back to Assessments</button>
        <div style={STYLES.errorContainer}>
          <strong>Error:</strong> {error || 'Assessment not found'}
        </div>
      </div>
    );
  }

  return (
    <div style={STYLES.container}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Roboto:wght@400;500;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Header */}
      <div style={STYLES.header}>
        <button style={STYLES.backButton} onClick={goBack}>← Back to Assessments</button>
        <h1 style={STYLES.title}>Quote Builder</h1>
        <div style={STYLES.subtitle}>Quote: {quoteNumber}</div>
        <div style={STYLES.subtitle}>Client: {assessment.client_name}</div>
        <div style={STYLES.subtitle}>Service: {assessment.service_type.toUpperCase().replace(/_/g, ' ')}</div>
      </div>

      {/* Assessment Summary */}
      <div style={STYLES.section}>
        <h2 style={STYLES.sectionTitle}>Assessment Details</h2>
        <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
          <div><strong>Address:</strong> {assessment.address || 'N/A'}</div>
          <div><strong>Contact:</strong> {assessment.contact_phone || 'N/A'}</div>
          {assessment.service_type === 'ev_charger' && assessment.additional_services?.ev_charger && (
            <>
              <div><strong>Circuit Size:</strong> {assessment.additional_services.ev_charger.circuit_size}A</div>
              <div><strong>Run Distance:</strong> {assessment.additional_services.ev_charger.run_distance_ft}ft</div>
              <div><strong>Load Management:</strong> {assessment.additional_services.ev_charger.load_management_required ? 'Yes' : 'No'}</div>
            </>
          )}
        </div>
      </div>

      {/* Materials */}
      {materialItems.length > 0 && (
        <div style={STYLES.section}>
          <h2 style={STYLES.sectionTitle}>Materials</h2>
          <table style={STYLES.table}>
            <thead>
              <tr style={STYLES.tableRow}>
                <td style={{ ...STYLES.tableHeader, width: '35%' }}>Item</td>
                <td style={{ ...STYLES.tableHeader, width: '15%' }}>Qty</td>
                <td style={{ ...STYLES.tableHeader, width: '20%' }}>Unit Cost</td>
                <td style={{ ...STYLES.tableHeader, width: '30%' }}>Line Total</td>
              </tr>
            </thead>
            <tbody>
              {materialItems.map(item => (
                <tr key={item.id} style={STYLES.tableRow}>
                  <td style={STYLES.tableCell}>
                    <input
                      type="text"
                      style={STYLES.input}
                      value={item.name}
                      onChange={(e) => updateLineItem(item.id, 'name', e.target.value)}
                    />
                  </td>
                  <td style={STYLES.tableCell}>
                    <input
                      type="number"
                      style={STYLES.input}
                      value={item.qty}
                      onChange={(e) => updateLineItem(item.id, 'qty', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td style={STYLES.tableCell}>
                    <input
                      type="number"
                      style={STYLES.input}
                      value={item.unitCost.toFixed(2)}
                      onChange={(e) => updateLineItem(item.id, 'unitCost', parseFloat(e.target.value) || 0)}
                      step="0.01"
                    />
                  </td>
                  <td style={STYLES.tableCell}>
                    <div style={{ marginBottom: '4px', fontWeight: '600', color: '#a5ce3a' }}>
                      ${item.lineTotal.toFixed(2)}
                    </div>
                    <button
                      style={{ ...STYLES.button, ...STYLES.buttonDanger }}
                      onClick={() => removeLineItem(item.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: '12px' }}>
            <div style={STYLES.totalsRow}>
              <div style={STYLES.totalsLabel}>Materials Subtotal:</div>
              <div>${materialSubtotal.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Labour */}
      {labourItems.length > 0 && (
        <div style={STYLES.section}>
          <h2 style={STYLES.sectionTitle}>Labour</h2>
          <table style={STYLES.table}>
            <thead>
              <tr style={STYLES.tableRow}>
                <td style={{ ...STYLES.tableHeader, width: '35%' }}>Task</td>
                <td style={{ ...STYLES.tableHeader, width: '15%' }}>Hours</td>
                <td style={{ ...STYLES.tableHeader, width: '20%' }}>Rate</td>
                <td style={{ ...STYLES.tableHeader, width: '30%' }}>Total</td>
              </tr>
            </thead>
            <tbody>
              {labourItems.map(item => (
                <tr key={item.id} style={STYLES.tableRow}>
                  <td style={STYLES.tableCell}>
                    <input
                      type="text"
                      style={STYLES.input}
                      value={item.name}
                      onChange={(e) => updateLineItem(item.id, 'name', e.target.value)}
                    />
                  </td>
                  <td style={STYLES.tableCell}>
                    <input
                      type="number"
                      style={STYLES.input}
                      value={item.qty}
                      onChange={(e) => updateLineItem(item.id, 'qty', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td style={STYLES.tableCell}>
                    <input
                      type="number"
                      style={STYLES.input}
                      value={item.unitCost.toFixed(2)}
                      onChange={(e) => updateLineItem(item.id, 'unitCost', parseFloat(e.target.value) || 0)}
                      step="0.01"
                    />
                  </td>
                  <td style={STYLES.tableCell}>
                    <div style={{ marginBottom: '4px', fontWeight: '600', color: '#a5ce3a' }}>
                      ${item.lineTotal.toFixed(2)}
                    </div>
                    <button
                      style={{ ...STYLES.button, ...STYLES.buttonDanger }}
                      onClick={() => removeLineItem(item.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: '12px' }}>
            <div style={STYLES.totalsRow}>
              <div style={STYLES.totalsLabel}>Labour Subtotal:</div>
              <div>${labourSubtotal.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Totals Summary */}
      <div style={STYLES.section}>
        <h2 style={STYLES.sectionTitle}>Summary</h2>
        <div style={STYLES.totalsRow}>
          <div style={STYLES.totalsLabel}>Materials:</div>
          <div>${materialSubtotal.toFixed(2)}</div>
        </div>
        <div style={STYLES.totalsRow}>
          <div style={STYLES.totalsLabel}>Labour:</div>
          <div>${labourSubtotal.toFixed(2)}</div>
        </div>
        <div style={STYLES.totalsRow}>
          <div style={STYLES.totalsLabel}>PST (7% on Materials):</div>
          <div>${pst.toFixed(2)}</div>
        </div>
        <div style={STYLES.grandTotalRow}>
          <div>Grand Total</div>
          <div>${grandTotal.toFixed(2)}</div>
        </div>
      </div>

      {/* Action Buttons — no Save: this legacy view is a scratch pad. Real quotes
          are saved by the Smart Assessment pipeline (smart_quotes, via api/). */}
      <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>
        Scratch view — edits here are not saved. Quotes are created through the Smart Assessment pipeline.
      </div>
      <div style={STYLES.buttonsContainer}>
        <button
          style={{ ...STYLES.button, ...STYLES.buttonSecondary }}
          onClick={addLineItem}
        >
          + Add Line Item
        </button>
        <button
          style={{ ...STYLES.button, ...STYLES.buttonSecondary }}
          onClick={goBack}
        >
          Cancel
        </button>
      </div>

      {/* Sync to Jobber — auto-fill custom fields */}
      {assessment?.job_id && (
        <div style={{ marginTop: '20px' }}>
          <SyncToJobberButton
            jobId={assessment.job_id}
            jobNumber={assessment.job_number || assessment.job_id}
            clientName={assessment.client_name}
          />
        </div>
      )}
      {!assessment?.job_id && (
        <div style={{ ...STYLES.section, marginTop: '20px', opacity: 0.5 }}>
          <div style={{ fontSize: 12, color: '#8892a4', textAlign: 'center', padding: 8 }}>
            Link this assessment to a Jobber job to enable auto-fill sync
          </div>
        </div>
      )}

      {/* Spacing */}
      <div style={{ height: '20px' }}></div>
    </div>
  );
}
